import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { PartyPopper, Package, UserCheck, ArrowLeft, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { SumTab, AutorizadosTab } from "./ClientePortal";
import { DwellingAuthsTab } from "./DwellingAuthsTab";

function dwellingLabel(user) {
  if (user?.dwelling_floor && user?.dwelling_unit) return `Piso ${user.dwelling_floor}, Depto ${user.dwelling_unit}`;
  if (user?.dwelling_block && user?.dwelling_house) return `Manzana ${user.dwelling_block}, Casa ${user.dwelling_house}`;
  return null;
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function CorreoTab({ clientId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setRows(null);
    try { const { data } = await api.get("/portal/correo"); setRows(data.filter((r) => r.client_id === clientId)); }
    catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };
  useEffect(() => { if (clientId) load(); }, [clientId]);

  const markPicked = async (id) => {
    setBusy(id);
    try { await api.patch(`/portal/correo/${id}/retirado`); toast.success("Marcado como retirado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (rows === null) return <div className="skel" style={{ height: 80 }} />;
  if (rows.length === 0) return <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }} data-testid="tenant-correo-empty">No tenés correo o paquetes en guardia.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="tenant-correo-list">
      {rows.map((r) => (
        <div key={r.id} className="card" data-testid={`tenant-correo-${r.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>{r.courier || "Correo / paquetería"}</div>
            <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: r.picked_up ? "#16a34a" : "#d97706" }}>{r.picked_up ? "Retirado" : "En guardia"}</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2 }}>Quedó en guardia: {fmtDate(r.created_at)}{r.site_name ? ` · ${r.site_name}` : ""}</div>
          {r.reason && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{r.reason}</div>}
          {r.picked_up ? (
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>Retirado: {fmtDate(r.picked_up_at)}</div>
          ) : (
            <button onClick={() => markPicked(r.id)} disabled={busy === r.id} className="btn-primary" style={{ marginTop: 8, padding: "7px 14px", fontSize: 12 }} data-testid={`tenant-correo-pickup-${r.id}`}>
              {busy === r.id ? "Guardando..." : "Ya lo retiré"}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Pantalla simple del inquilino: accesos grandes tipo tarjeta a Reservas,
 * Autorizaciones, Acceso Visitas y Correo — pensada para celular y alguien
 * que no es del rubro. Nunca muestra cronograma/equipos/rondas (eso es del
 * consorcio/administración, no de un vecino individual). */
export default function TenantPortal({ objetivos }) {
  const { user } = useAuth();
  const [clientId, setClientId] = useState(objetivos[0]?.client_id || "");
  const [view, setView] = useState("home");
  const current = objetivos.find((o) => o.client_id === clientId) || objetivos[0];
  const dwelling = dwellingLabel(user);

  const cards = [
    ...(current?.sum_enabled ? [{ key: "reservas", label: "Reservas", desc: `Reservar ${current.sum_name || "el SUM"}`, icon: PartyPopper }] : []),
    { key: "autorizaciones", label: "Autorizaciones", desc: "Retiro o ingreso de elementos", icon: Package },
    { key: "visitas", label: "Acceso Visitas", desc: "Personal autorizado a ingresar", icon: UserCheck },
    { key: "correo", label: "Correo", desc: "Paquetes que quedaron en guardia", icon: Mail },
  ];

  return (
    <div style={{ padding: "10px 16px 40px", maxWidth: 640, margin: "0 auto" }} data-testid="tenant-portal-page">
      <div style={{ background: "linear-gradient(135deg, #0A0A0A 0%, #1a0e0f 100%)", color: "#FFFBF4", padding: "22px 18px", borderRadius: 4, marginBottom: 16 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase", marginBottom: 6 }}>Portal del Inquilino</div>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: 0, letterSpacing: "-0.02em" }}>
          Hola, <span style={{ color: "#BF3437" }}>{(user?.name || "").split(" ")[0] || "vecino"}</span>.
        </h1>
        <div style={{ fontSize: 13, color: "rgba(255,251,244,0.75)", marginTop: 8 }} data-testid="tenant-dwelling-label">
          {current?.client_name}{dwelling ? ` · ${dwelling}` : ""}
        </div>
        {objetivos.length > 1 && (
          <select value={clientId} onChange={(e) => { setClientId(e.target.value); setView("home"); }}
            style={{ marginTop: 10, background: "rgba(255,255,255,0.08)", color: "#FFFBF4", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 4, padding: "6px 10px" }}
            data-testid="tenant-select-objetivo">
            {objetivos.map((o) => <option key={o.client_id} value={o.client_id}>{o.client_name}</option>)}
          </select>
        )}
      </div>

      {view === "home" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }} data-testid="tenant-home-cards">
          {cards.map((c) => (
            <button key={c.key} onClick={() => setView(c.key)} className="card"
              style={{ padding: 26, textAlign: "center", cursor: "pointer", border: "1px solid var(--invict-bone-3)" }}
              data-testid={`tenant-card-${c.key}`}>
              <c.icon size={30} style={{ color: "#BF3437", margin: "0 auto 10px" }} />
              <div style={{ fontWeight: 900, fontSize: 15, color: "#0A0A0A" }}>{c.label}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>{c.desc}</div>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <button onClick={() => setView("home")} className="btn-ghost" style={{ marginBottom: 14, padding: "8px 14px", fontSize: 12 }} data-testid="tenant-back-home">
            <ArrowLeft size={13} style={{ marginRight: 6 }} />Volver
          </button>
          {view === "reservas" && <SumTab clientId={clientId} siteId="" sumName={current?.sum_name || "SUM"} isClienteAdmin={false} userId={user?.id} />}
          {view === "autorizaciones" && <DwellingAuthsTab clientId={clientId} />}
          {view === "visitas" && <AutorizadosTab clientId={clientId} />}
          {view === "correo" && <CorreoTab clientId={clientId} />}
        </div>
      )}
    </div>
  );
}
